/**
 * HPRMS company-level module configuration.
 * Phase 1a: modules (sidebar). Phase 1c: nested pages per module.
 * Source of truth: localStorage. Redux holds the active copy after login.
 */

export const MODULE_SETTINGS_PATH = "/configuration/module-settings";

export const HPRMS_MODULE_KEYS = {
  DASHBOARD: "dashboard",
  PURCHASE_REQUESTS: "purchase_requests",
  RFQ: "rfq",
  PURCHASE_ORDERS: "purchase_orders",
  VENDOR_BILLS: "vendor_bills",
  VENDOR_PAYMENTS: "vendor_payments",
  VENDORS: "vendors",
  PRODUCTS: "products",
  CATEGORIES: "categories",
  INVENTORY: "inventory",
  GOODS_RECEIPTS: "goods_receipts",
  QUALITY_INSPECTIONS: "quality_inspections",
  PURCHASE_RETURNS: "purchase_returns",
  GRN_LEGACY: "grn_legacy",
  REPORTS_PURCHASES: "reports_purchases",
  REPORTS_INVENTORY: "reports_inventory",
  DATA_WIZARD: "data_wizard",
  MODULE_SETTINGS: "module_settings",
};

/**
 * Nested pages (L2) under each module.
 * match: "prefix" | "exact" | "detail" (module path that is not new/edit/compare/etc.)
 */
export const HPRMS_PAGE_DEFINITIONS_BY_MODULE = {
  [HPRMS_MODULE_KEYS.DASHBOARD]: [],
  [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]: [
    {
      key: "purchase_requests.new",
      label: "New request",
      match: "exact",
      paths: ["/PurchaseRequest/New"],
    },
    {
      key: "purchase_requests.edit",
      label: "Edit request",
      match: "prefix",
      paths: ["/PurchaseRequest/Edit"],
    },
    {
      key: "purchase_requests.detail",
      label: "Request detail",
      match: "detail",
      base: "/PurchaseRequest",
      excludePrefixes: ["/PurchaseRequest/New", "/PurchaseRequest/Edit"],
    },
  ],
  [HPRMS_MODULE_KEYS.RFQ]: [
    { key: "rfq.new", label: "New RFQ", match: "exact", paths: ["/RFQ/New"] },
    {
      key: "rfq.compare",
      label: "Compare quotations",
      match: "suffix",
      paths: ["/Compare"],
      mustInclude: "/RFQ/",
    },
    {
      // Only "create new VQ". Viewing an existing VQ is part of RFQ detail flow (no page gate).
      key: "rfq.vendor_quotation",
      label: "Add vendor quotation",
      match: "exact",
      paths: ["/VendorQuotations/New"],
    },
    {
      key: "rfq.detail",
      label: "RFQ / quotation detail",
      match: "detail",
      base: "/RFQ",
      excludePrefixes: ["/RFQ/New"],
      excludeSuffixes: ["/Compare"],
    },
  ],
  [HPRMS_MODULE_KEYS.PURCHASE_ORDERS]: [
    {
      // Not a sidebar item — only New Order on PO list + direct create route.
      key: "purchase_orders.create",
      label: "Create / New Purchase Order (New Order button)",
      match: "prefix",
      paths: ["/ViewPurchaseOrders"],
    },
    {
      key: "purchase_orders.detail",
      label: "PO detail page",
      match: "prefix",
      paths: ["/PurchaseOrder"],
    },
  ],
  [HPRMS_MODULE_KEYS.VENDOR_BILLS]: [
    {
      key: "vendor_bills.new",
      label: "New vendor bill",
      match: "exact",
      paths: ["/VendorBill/New"],
    },
    {
      key: "vendor_bills.detail",
      label: "Bill detail / edit",
      match: "detail",
      base: "/VendorBill",
      excludePrefixes: ["/VendorBill/New"],
    },
  ],
  [HPRMS_MODULE_KEYS.VENDOR_PAYMENTS]: [
    {
      key: "vendor_payments.new",
      label: "New payment",
      match: "exact",
      paths: [], // same-route UI mode — button only, not a separate URL
    },
    {
      key: "vendor_payments.detail",
      label: "Payment detail",
      match: "exact",
      paths: [], // same-route UI mode — detail panel on /VendorPayments
    },
  ],
  [HPRMS_MODULE_KEYS.VENDORS]: [
    {
      key: "vendors.add",
      label: "Add vendor",
      match: "exact",
      paths: ["/addVendor", "/editVendor", "/Vendor"],
      menuPaths: ["/addVendor"],
    },
  ],
  [HPRMS_MODULE_KEYS.PRODUCTS]: [
    {
      key: "products.create",
      label: "Add product",
      match: "exact",
      paths: ["/ProductForm"],
    },
    {
      key: "products.edit",
      label: "Edit product",
      match: "prefix",
      paths: ["/editproduct"],
    },
  ],
  [HPRMS_MODULE_KEYS.CATEGORIES]: [
    {
      key: "categories.add",
      label: "Add category",
      match: "prefix",
      paths: ["/Category/Add", "/Category"],
    },
  ],
  [HPRMS_MODULE_KEYS.INVENTORY]: [
    {
      key: "inventory.history",
      label: "Inventory history",
      match: "prefix",
      paths: ["/inventoryHistory", "/inventryHistory"],
    },
  ],
  [HPRMS_MODULE_KEYS.GOODS_RECEIPTS]: [
    {
      key: "goods_receipts.new",
      label: "New goods receipt",
      match: "exact",
      paths: ["/GoodsReceipt/New"],
    },
    {
      key: "goods_receipts.detail",
      label: "Goods receipt detail",
      match: "detail",
      base: "/GoodsReceipt",
      excludePrefixes: ["/GoodsReceipt/New"],
    },
  ],
  [HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS]: [
    {
      key: "quality_inspections.new",
      label: "New inspection",
      match: "exact",
      paths: ["/QualityInspection/New"],
    },
    {
      key: "quality_inspections.detail",
      label: "Inspection detail",
      match: "detail",
      base: "/QualityInspection",
      excludePrefixes: ["/QualityInspection/New"],
    },
  ],
  [HPRMS_MODULE_KEYS.PURCHASE_RETURNS]: [
    {
      key: "purchase_returns.new",
      label: "New return",
      match: "exact",
      paths: ["/PurchaseReturn/New"],
    },
    {
      key: "purchase_returns.detail",
      label: "Return detail",
      match: "detail",
      base: "/PurchaseReturn",
      excludePrefixes: ["/PurchaseReturn/New"],
    },
  ],
  [HPRMS_MODULE_KEYS.GRN_LEGACY]: [],
  [HPRMS_MODULE_KEYS.REPORTS_PURCHASES]: [],
  [HPRMS_MODULE_KEYS.REPORTS_INVENTORY]: [],
  [HPRMS_MODULE_KEYS.DATA_WIZARD]: [],
  [HPRMS_MODULE_KEYS.MODULE_SETTINGS]: [],
};

/**
 * Phase 2 — optional field groups (L3).
 * Defaults ON. OFF = hide on create/edit UI; existing values are kept on save.
 * Create-vendor field groups are deferred (not listed yet).
 */
export const HPRMS_FIELD_GROUP_KEYS = {
  PRODUCTS_TAX_DISCOUNT: "products.tax_discount",
  PRODUCTS_EXTRA_ATTRS: "products.extra_attrs",
  PRODUCTS_IMAGES: "products.images",
  PURCHASE_REQUESTS_HEADER: "purchase_requests.header",
  RFQ_OPTIONAL: "rfq.optional_fields",
  VQ_OPTIONAL: "rfq.vq_optional_fields",
  INVENTORY_LIST_ACTIONS: "inventory.list_actions",
};

export const HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE = {
  [HPRMS_MODULE_KEYS.PRODUCTS]: [
    {
      key: HPRMS_FIELD_GROUP_KEYS.PRODUCTS_TAX_DISCOUNT,
      label: "Tax % / Discount %",
      description: "Optional pricing fields on create / edit product",
    },
    {
      key: HPRMS_FIELD_GROUP_KEYS.PRODUCTS_EXTRA_ATTRS,
      label: "Extra attributes",
      description: "Manufacturer, Supplier, SKU, Expiry date",
    },
    {
      key: HPRMS_FIELD_GROUP_KEYS.PRODUCTS_IMAGES,
      label: "Product images",
      description: "Image upload on create / edit product",
    },
  ],
  [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]: [
    {
      key: HPRMS_FIELD_GROUP_KEYS.PURCHASE_REQUESTS_HEADER,
      label: "Header fields",
      description: "Department, Requested By, Needed By, Notes (product lines stay required)",
    },
  ],
  [HPRMS_MODULE_KEYS.RFQ]: [
    {
      key: HPRMS_FIELD_GROUP_KEYS.RFQ_OPTIONAL,
      label: "RFQ optional fields",
      description: "Valid Until, Notes, Invite vendors (products stay required)",
    },
    {
      key: HPRMS_FIELD_GROUP_KEYS.VQ_OPTIONAL,
      label: "Vendor quotation terms",
      description:
        "Payment Terms, Delivery Days, Header Discount, Valid Until, Notes (vendor + products stay required)",
    },
  ],
  [HPRMS_MODULE_KEYS.INVENTORY]: [
    {
      key: HPRMS_FIELD_GROUP_KEYS.INVENTORY_LIST_ACTIONS,
      label: "Actions column",
      description: "Add / adjust inventory on the Inventory list",
    },
  ],
};

/** @deprecated Use HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE */
export const HPRMS_FIELD_GROUP_PLACEHOLDERS_BY_MODULE = {
  [HPRMS_MODULE_KEYS.PRODUCTS]:
    HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[HPRMS_MODULE_KEYS.PRODUCTS].map((g) => g.label),
  [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]:
    HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[HPRMS_MODULE_KEYS.PURCHASE_REQUESTS].map(
      (g) => g.label
    ),
  [HPRMS_MODULE_KEYS.RFQ]: HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[HPRMS_MODULE_KEYS.RFQ].map(
    (g) => g.label
  ),
  [HPRMS_MODULE_KEYS.INVENTORY]:
    HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[HPRMS_MODULE_KEYS.INVENTORY].map((g) => g.label),
};

/** UI metadata for Module Settings page (order = display order). */
export const HPRMS_MODULE_DEFINITIONS = [
  {
    key: HPRMS_MODULE_KEYS.DASHBOARD,
    label: "Dashboard",
    section: "DASHBOARD",
    locked: true,
    summary: "Home screen after login",
  },
  {
    key: HPRMS_MODULE_KEYS.PURCHASE_REQUESTS,
    label: "Purchase Requests",
    section: "PROCUREMENT",
    summary: "Internal requests before RFQ / PO (needs Products)",
  },
  {
    key: HPRMS_MODULE_KEYS.RFQ,
    label: "RFQs",
    section: "PROCUREMENT",
    summary: "Ask vendors for quotes, compare, pick winner (needs Vendors, Products, Purchase Orders)",
    configureNote:
      "Add vendor quotation controls creating new quotes. Opening an existing quotation works when RFQ is on. Convert / Open PO needs Purchase Orders on.",
  },
  {
    key: HPRMS_MODULE_KEYS.PURCHASE_ORDERS,
    label: "Purchase Orders",
    section: "PROCUREMENT",
    summary: "Create and manage POs (needs Products)",
    configureNote:
      "Vendor picker on Create PO appears only when the Vendors module is ON. Products are required for order lines.",
  },
  {
    key: HPRMS_MODULE_KEYS.VENDOR_BILLS,
    label: "Vendor Bills",
    section: "PROCUREMENT",
    summary: "Supplier invoices against POs",
  },
  {
    key: HPRMS_MODULE_KEYS.VENDOR_PAYMENTS,
    label: "Vendor Payments",
    section: "PROCUREMENT",
    summary: "Pay vendor bills",
  },
  {
    key: HPRMS_MODULE_KEYS.VENDORS,
    label: "Vendors",
    section: "PROCUREMENT",
    summary: "Vendor list in sidebar + vendor picker on Create PO / RFQ",
    configureNote:
      "When OFF: Vendors menu is hidden and the vendor field is hidden on Create Purchase Order.",
  },
  {
    key: HPRMS_MODULE_KEYS.PRODUCTS,
    label: "Products",
    section: "PRODUCT CATALOG",
    summary: "Product catalogue (needs Categories)",
  },
  {
    key: HPRMS_MODULE_KEYS.CATEGORIES,
    label: "Categories",
    section: "PRODUCT CATALOG",
    summary: "Product categories",
  },
  {
    key: HPRMS_MODULE_KEYS.INVENTORY,
    label: "Inventory",
    section: "STOCK OPERATIONS",
    summary: "Stock levels and adjustments",
  },
  {
    key: HPRMS_MODULE_KEYS.GOODS_RECEIPTS,
    label: "Goods Receipts",
    section: "STOCK OPERATIONS",
    summary: "Receive PO goods into stock",
  },
  {
    key: HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS,
    label: "Quality Inspections",
    section: "STOCK OPERATIONS",
    summary: "Inspect received goods",
  },
  {
    key: HPRMS_MODULE_KEYS.PURCHASE_RETURNS,
    label: "Purchase Returns",
    section: "STOCK OPERATIONS",
    summary: "Return goods to vendor",
  },
  {
    key: HPRMS_MODULE_KEYS.GRN_LEGACY,
    label: "GRN (legacy)",
    section: "STOCK OPERATIONS",
    summary: "Old GRN screen — prefer Goods Receipts",
  },
  {
    key: HPRMS_MODULE_KEYS.REPORTS_PURCHASES,
    label: "Purchase Overview",
    section: "REPORTS",
    summary: "Purchase reporting",
  },
  {
    key: HPRMS_MODULE_KEYS.REPORTS_INVENTORY,
    label: "Inventory Overview",
    section: "REPORTS",
    summary: "Inventory reporting",
  },
  {
    key: HPRMS_MODULE_KEYS.DATA_WIZARD,
    label: "Data Import/Export",
    section: "SETTINGS",
    summary: "Import / export data",
  },
  {
    key: HPRMS_MODULE_KEYS.MODULE_SETTINGS,
    label: "Module Configuration",
    section: "SETTINGS",
    locked: true,
    summary: "This settings page",
  },
];

export const HPRMS_SECTION_HELP = {
  DASHBOARD: "Main home",
  PROCUREMENT: "Buying flow — turn a main module on, then Configure for pages inside it",
  "PRODUCT CATALOG": "Catalogue used on purchase order lines",
  "STOCK OPERATIONS": "Receiving, inspection, returns, stock",
  REPORTS: "Overview reports",
  SETTINGS: "App setup",
};

/** Sidebar / route path → module key */
const PATH_MODULE_RULES = [
  { prefix: MODULE_SETTINGS_PATH, moduleKey: HPRMS_MODULE_KEYS.MODULE_SETTINGS },
  { prefix: "/configuration/data-wizard", moduleKey: HPRMS_MODULE_KEYS.DATA_WIZARD },
  { prefix: "/reports/dashboard", moduleKey: HPRMS_MODULE_KEYS.DASHBOARD },
  { prefix: "/reports/purchases", moduleKey: HPRMS_MODULE_KEYS.REPORTS_PURCHASES },
  { prefix: "/reports/inventory", moduleKey: HPRMS_MODULE_KEYS.REPORTS_INVENTORY },
  { prefix: "/PurchaseRequests", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_REQUESTS },
  { prefix: "/PurchaseRequest", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_REQUESTS },
  { prefix: "/RFQs", moduleKey: HPRMS_MODULE_KEYS.RFQ },
  { prefix: "/RFQ", moduleKey: HPRMS_MODULE_KEYS.RFQ },
  { prefix: "/VendorQuotations", moduleKey: HPRMS_MODULE_KEYS.RFQ },
  { prefix: "/VendorQuotation", moduleKey: HPRMS_MODULE_KEYS.RFQ },
  { prefix: "/Purchase Orders", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_ORDERS },
  { prefix: "/PurchaseOrder", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_ORDERS },
  { prefix: "/ViewPurchaseOrders", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_ORDERS },
  { prefix: "/VendorBills", moduleKey: HPRMS_MODULE_KEYS.VENDOR_BILLS },
  { prefix: "/VendorBill", moduleKey: HPRMS_MODULE_KEYS.VENDOR_BILLS },
  { prefix: "/VendorPayments", moduleKey: HPRMS_MODULE_KEYS.VENDOR_PAYMENTS },
  { prefix: "/Vendors", moduleKey: HPRMS_MODULE_KEYS.VENDORS },
  { prefix: "/addVendor", moduleKey: HPRMS_MODULE_KEYS.VENDORS },
  { prefix: "/editVendor", moduleKey: HPRMS_MODULE_KEYS.VENDORS },
  { prefix: "/Vendor", moduleKey: HPRMS_MODULE_KEYS.VENDORS },
  { prefix: "/Products", moduleKey: HPRMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/ProductForm", moduleKey: HPRMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/editproduct", moduleKey: HPRMS_MODULE_KEYS.PRODUCTS },
  { prefix: "/Categories", moduleKey: HPRMS_MODULE_KEYS.CATEGORIES },
  { prefix: "/Category", moduleKey: HPRMS_MODULE_KEYS.CATEGORIES },
  { prefix: "/inventoryHistory", moduleKey: HPRMS_MODULE_KEYS.INVENTORY },
  { prefix: "/inventryHistory", moduleKey: HPRMS_MODULE_KEYS.INVENTORY },
  { prefix: "/inventory", moduleKey: HPRMS_MODULE_KEYS.INVENTORY },
  { prefix: "/GoodsReceipts", moduleKey: HPRMS_MODULE_KEYS.GOODS_RECEIPTS },
  { prefix: "/GoodsReceipt", moduleKey: HPRMS_MODULE_KEYS.GOODS_RECEIPTS },
  { prefix: "/QualityInspections", moduleKey: HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS },
  { prefix: "/QualityInspection", moduleKey: HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS },
  { prefix: "/PurchaseReturns", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_RETURNS },
  { prefix: "/PurchaseReturn", moduleKey: HPRMS_MODULE_KEYS.PURCHASE_RETURNS },
  { prefix: "/grn", moduleKey: HPRMS_MODULE_KEYS.GRN_LEGACY },
];

function getAllPageDefinitions() {
  return Object.values(HPRMS_PAGE_DEFINITIONS_BY_MODULE).flat();
}

export function getDefaultModules() {
  const modules = {};
  HPRMS_MODULE_DEFINITIONS.forEach(({ key }) => {
    modules[key] = true;
  });
  modules[HPRMS_MODULE_KEYS.GRN_LEGACY] = false;
  modules[HPRMS_MODULE_KEYS.DASHBOARD] = true;
  modules[HPRMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return modules;
}

export function getDefaultPages() {
  const pages = {};
  getAllPageDefinitions().forEach((page) => {
    pages[page.key] = true;
  });
  return pages;
}

export function getAllFieldGroupDefinitions() {
  return Object.values(HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE).flat();
}

export function getDefaultFieldGroups() {
  const fieldGroups = {};
  getAllFieldGroupDefinitions().forEach((group) => {
    fieldGroups[group.key] = true;
  });
  return fieldGroups;
}

export function getDefaultModuleConfiguration(meta = {}) {
  return {
    companyName: meta.companyName || "",
    instanceId: meta.instanceId || "",
    appType: "HPRMS",
    modules: getDefaultModules(),
    pages: getDefaultPages(),
    fieldGroups: getDefaultFieldGroups(),
    updatedAt: null,
  };
}

export function getModuleConfigStorageKey(companyName, instanceId, appType = "HPRMS") {
  const company = companyName || "default";
  const instance = instanceId || "default";
  const app = (appType || "HPRMS").replace(/-$/, "");
  return `hulm_module_config_${company}_${instance}_${app}`;
}

export function normalizeModuleConfiguration(config, meta = {}) {
  const defaults = getDefaultModuleConfiguration(meta);
  if (!config || typeof config !== "object") return defaults;

  const modules = { ...defaults.modules };
  const incomingModules =
    config.modules && typeof config.modules === "object" ? config.modules : {};

  Object.keys(modules).forEach((key) => {
    if (typeof incomingModules[key] === "boolean") {
      modules[key] = incomingModules[key];
    }
  });

  modules[HPRMS_MODULE_KEYS.DASHBOARD] = true;
  modules[HPRMS_MODULE_KEYS.MODULE_SETTINGS] = true;

  const pages = { ...defaults.pages };
  const incomingPages = config.pages && typeof config.pages === "object" ? config.pages : {};
  Object.keys(pages).forEach((key) => {
    if (typeof incomingPages[key] === "boolean") {
      pages[key] = incomingPages[key];
    }
  });

  // Never persist / load broken combos (e.g. Products ON, Categories OFF).
  const enforced = enforceModuleDependencies(modules);

  const fieldGroups = { ...defaults.fieldGroups };
  const incomingFieldGroups =
    config.fieldGroups && typeof config.fieldGroups === "object" ? config.fieldGroups : {};
  Object.keys(fieldGroups).forEach((key) => {
    if (typeof incomingFieldGroups[key] === "boolean") {
      fieldGroups[key] = incomingFieldGroups[key];
    }
  });

  return {
    companyName: config.companyName || meta.companyName || defaults.companyName,
    instanceId: config.instanceId || meta.instanceId || defaults.instanceId,
    appType: "HPRMS",
    modules: enforced.modules,
    pages,
    fieldGroups,
    updatedAt: config.updatedAt || null,
  };
}

export function loadModuleConfiguration(companyName, instanceId, appType = "HPRMS") {
  const meta = { companyName, instanceId, appType };
  if (!companyName || !instanceId) {
    return getDefaultModuleConfiguration(meta);
  }

  try {
    const raw = localStorage.getItem(getModuleConfigStorageKey(companyName, instanceId, appType));
    if (!raw) return getDefaultModuleConfiguration(meta);
    return normalizeModuleConfiguration(JSON.parse(raw), meta);
  } catch {
    return getDefaultModuleConfiguration(meta);
  }
}

export function saveModuleConfiguration(companyName, instanceId, config, appType = "HPRMS") {
  const normalized = normalizeModuleConfiguration(config, { companyName, instanceId, appType });
  const toStore = {
    ...normalized,
    companyName,
    instanceId,
    appType: "HPRMS",
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(
    getModuleConfigStorageKey(companyName, instanceId, appType),
    JSON.stringify(toStore)
  );
  return toStore;
}

export function isModuleEnabled(modules, moduleKey) {
  if (!moduleKey) return true;
  if (
    moduleKey === HPRMS_MODULE_KEYS.MODULE_SETTINGS ||
    moduleKey === HPRMS_MODULE_KEYS.DASHBOARD
  ) {
    return true;
  }
  if (!modules || typeof modules !== "object") return true;
  return modules[moduleKey] !== false;
}

export function isPageEnabled(pages, pageKey) {
  if (!pageKey) return true;
  if (!pages || typeof pages !== "object") return true;
  return pages[pageKey] !== false;
}

/** Optional field groups default ON when missing. */
export function isFieldGroupEnabled(fieldGroups, groupKey) {
  if (!groupKey) return true;
  if (!fieldGroups || typeof fieldGroups !== "object") return true;
  return fieldGroups[groupKey] !== false;
}

function pathMatchesPageRule(path, rule) {
  if (!rule) return false;

  if (rule.match === "exact") {
    return (rule.paths || []).some((p) => path === p);
  }

  if (rule.match === "prefix") {
    return (rule.paths || []).some(
      (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(p)
    );
  }

  if (rule.match === "suffix") {
    const okSuffix = (rule.paths || []).some((p) => path.endsWith(p));
    if (!okSuffix) return false;
    if (rule.mustInclude && !path.includes(rule.mustInclude)) return false;
    return true;
  }

  if (rule.match === "detail") {
    const base = rule.base;
    if (!base) return false;
    if (path === base || path === `${base}s`) return false;
    if (!(path === base || path.startsWith(`${base}/`))) return false;
    const excluded = (rule.excludePrefixes || []).some(
      (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(p)
    );
    if (excluded) return false;
    const excludedSuffix = (rule.excludeSuffixes || []).some((s) => path.endsWith(s));
    if (excludedSuffix) return false;
    return true;
  }

  return false;
}

export function getPageKeyForPath(pathname) {
  if (!pathname) return null;
  const path = pathname.split("?")[0];
  const allPages = getAllPageDefinitions();

  for (const rule of allPages) {
    if (pathMatchesPageRule(path, rule)) {
      return rule.key;
    }
  }
  return null;
}

export function getModuleKeyForPath(pathname) {
  if (!pathname) return null;
  const path = pathname.split("?")[0];

  const rules = [...PATH_MODULE_RULES].sort((a, b) => b.prefix.length - a.prefix.length);

  for (const rule of rules) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      return rule.moduleKey;
    }
    if (path.startsWith(rule.prefix) && rule.prefix.includes(" ")) {
      return rule.moduleKey;
    }
  }
  return null;
}

export function isHprmsPathEnabled(pathname, modules, pages) {
  const moduleKey = getModuleKeyForPath(pathname);
  if (moduleKey && !isModuleEnabled(modules, moduleKey)) {
    return false;
  }

  const pageKey = getPageKeyForPath(pathname);
  if (pageKey && !isPageEnabled(pages, pageKey)) {
    return false;
  }

  return true;
}

export function getHprmsFallbackPath(modules) {
  if (isModuleEnabled(modules, HPRMS_MODULE_KEYS.DASHBOARD)) {
    return "/reports/dashboard";
  }
  return MODULE_SETTINGS_PATH;
}

/** Filter sidebar sections for HPRMS using module + page flags. */
export function filterHprmsMenuSections(sections, modules, pages) {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => {
        if (item.action) return true;
        if (!item.path) return true;
        return isHprmsPathEnabled(item.path, modules, pages);
      }),
    }))
    .filter((section) => section.items.length > 0);
}

export function getPagesForModule(moduleKey) {
  return HPRMS_PAGE_DEFINITIONS_BY_MODULE[moduleKey] || [];
}

export function getFieldGroupsForModule(moduleKey) {
  return HPRMS_FIELD_GROUP_DEFINITIONS_BY_MODULE[moduleKey] || [];
}

export function getFieldGroupPlaceholdersForModule(moduleKey) {
  return getFieldGroupsForModule(moduleKey).map((g) => g.label);
}

export function moduleHasConfigurableDetails(moduleKey) {
  return (
    getPagesForModule(moduleKey).length > 0 || getFieldGroupsForModule(moduleKey).length > 0
  );
}

export function isHprmsAppType(appType) {
  if (!appType) return false;
  return appType.replace(/-$/, "") === "HPRMS";
}

function moduleLabel(key) {
  const def = HPRMS_MODULE_DEFINITIONS.find((d) => d.key === key);
  return def?.label || key;
}

/**
 * Hard dependencies — invalid combos break procurement flows.
 * `moduleKey` ON requires every key in `requires` also ON.
 */
export const HPRMS_MODULE_DEPENDENCIES = [
  {
    moduleKey: HPRMS_MODULE_KEYS.PRODUCTS,
    requires: [HPRMS_MODULE_KEYS.CATEGORIES],
    reason: "Products use Categories to organize the catalogue.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.PURCHASE_REQUESTS,
    requires: [HPRMS_MODULE_KEYS.PRODUCTS],
    reason: "Purchase Requests need Products on the request lines.",
  },
  {
    // Vendors stay optional on Create PO — only products (and thus categories) are required.
    moduleKey: HPRMS_MODULE_KEYS.PURCHASE_ORDERS,
    requires: [HPRMS_MODULE_KEYS.PRODUCTS],
    reason: "Purchase Orders need Products on the order lines.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.GOODS_RECEIPTS,
    requires: [HPRMS_MODULE_KEYS.PURCHASE_ORDERS],
    reason: "Goods Receipts receive stock against Purchase Orders.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS,
    requires: [HPRMS_MODULE_KEYS.GOODS_RECEIPTS],
    reason: "Quality Inspections check goods after a Goods Receipt.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.PURCHASE_RETURNS,
    requires: [HPRMS_MODULE_KEYS.GOODS_RECEIPTS],
    reason: "Purchase Returns send goods back from a Goods Receipt.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.VENDOR_BILLS,
    requires: [HPRMS_MODULE_KEYS.VENDORS],
    reason: "Vendor Bills are linked to Vendors.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.VENDOR_PAYMENTS,
    requires: [HPRMS_MODULE_KEYS.VENDORS],
    reason: "Vendor Payments are linked to Vendors.",
  },
  {
    moduleKey: HPRMS_MODULE_KEYS.RFQ,
    requires: [
      HPRMS_MODULE_KEYS.VENDORS,
      HPRMS_MODULE_KEYS.PRODUCTS,
      HPRMS_MODULE_KEYS.PURCHASE_ORDERS,
    ],
    reason:
      "RFQs invite Vendors, request Products, and convert the winner into a Purchase Order.",
  },
];

/** Quick-start packs for Module Settings. */
export const HPRMS_MODULE_PACKS = [
  {
    id: "simple_buying",
    label: "Simple buying",
    description: "PO, vendors, products, inventory, goods receipts",
    modules: {
      [HPRMS_MODULE_KEYS.DASHBOARD]: true,
      [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]: false,
      [HPRMS_MODULE_KEYS.RFQ]: false,
      [HPRMS_MODULE_KEYS.PURCHASE_ORDERS]: true,
      [HPRMS_MODULE_KEYS.VENDOR_BILLS]: false,
      [HPRMS_MODULE_KEYS.VENDOR_PAYMENTS]: false,
      [HPRMS_MODULE_KEYS.VENDORS]: true,
      [HPRMS_MODULE_KEYS.PRODUCTS]: true,
      [HPRMS_MODULE_KEYS.CATEGORIES]: true,
      [HPRMS_MODULE_KEYS.INVENTORY]: true,
      [HPRMS_MODULE_KEYS.GOODS_RECEIPTS]: true,
      [HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS]: false,
      [HPRMS_MODULE_KEYS.PURCHASE_RETURNS]: false,
      [HPRMS_MODULE_KEYS.GRN_LEGACY]: false,
      [HPRMS_MODULE_KEYS.REPORTS_PURCHASES]: false,
      [HPRMS_MODULE_KEYS.REPORTS_INVENTORY]: false,
      [HPRMS_MODULE_KEYS.DATA_WIZARD]: true,
      [HPRMS_MODULE_KEYS.MODULE_SETTINGS]: true,
    },
  },
  {
    id: "full_procurement",
    label: "Full procurement",
    description: "Full PR → RFQ → PO → GR → QI → bills flow",
    modules: {
      [HPRMS_MODULE_KEYS.DASHBOARD]: true,
      [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]: true,
      [HPRMS_MODULE_KEYS.RFQ]: true,
      [HPRMS_MODULE_KEYS.PURCHASE_ORDERS]: true,
      [HPRMS_MODULE_KEYS.VENDOR_BILLS]: true,
      [HPRMS_MODULE_KEYS.VENDOR_PAYMENTS]: true,
      [HPRMS_MODULE_KEYS.VENDORS]: true,
      [HPRMS_MODULE_KEYS.PRODUCTS]: true,
      [HPRMS_MODULE_KEYS.CATEGORIES]: true,
      [HPRMS_MODULE_KEYS.INVENTORY]: true,
      [HPRMS_MODULE_KEYS.GOODS_RECEIPTS]: true,
      [HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS]: true,
      [HPRMS_MODULE_KEYS.PURCHASE_RETURNS]: true,
      [HPRMS_MODULE_KEYS.GRN_LEGACY]: false,
      [HPRMS_MODULE_KEYS.REPORTS_PURCHASES]: true,
      [HPRMS_MODULE_KEYS.REPORTS_INVENTORY]: true,
      [HPRMS_MODULE_KEYS.DATA_WIZARD]: true,
      [HPRMS_MODULE_KEYS.MODULE_SETTINGS]: true,
    },
  },
  {
    id: "catalog_only",
    label: "Catalog only",
    description: "Products, categories, inventory",
    modules: {
      [HPRMS_MODULE_KEYS.DASHBOARD]: true,
      [HPRMS_MODULE_KEYS.PURCHASE_REQUESTS]: false,
      [HPRMS_MODULE_KEYS.RFQ]: false,
      [HPRMS_MODULE_KEYS.PURCHASE_ORDERS]: false,
      [HPRMS_MODULE_KEYS.VENDOR_BILLS]: false,
      [HPRMS_MODULE_KEYS.VENDOR_PAYMENTS]: false,
      [HPRMS_MODULE_KEYS.VENDORS]: false,
      [HPRMS_MODULE_KEYS.PRODUCTS]: true,
      [HPRMS_MODULE_KEYS.CATEGORIES]: true,
      [HPRMS_MODULE_KEYS.INVENTORY]: true,
      [HPRMS_MODULE_KEYS.GOODS_RECEIPTS]: false,
      [HPRMS_MODULE_KEYS.QUALITY_INSPECTIONS]: false,
      [HPRMS_MODULE_KEYS.PURCHASE_RETURNS]: false,
      [HPRMS_MODULE_KEYS.GRN_LEGACY]: false,
      [HPRMS_MODULE_KEYS.REPORTS_PURCHASES]: false,
      [HPRMS_MODULE_KEYS.REPORTS_INVENTORY]: false,
      [HPRMS_MODULE_KEYS.DATA_WIZARD]: true,
      [HPRMS_MODULE_KEYS.MODULE_SETTINGS]: true,
    },
  },
];

export function applyModulePack(packId, currentModules = {}) {
  const pack = HPRMS_MODULE_PACKS.find((p) => p.id === packId);
  if (!pack) return { ...currentModules };
  const next = {
    ...getDefaultModules(),
    ...currentModules,
    ...pack.modules,
  };
  next[HPRMS_MODULE_KEYS.DASHBOARD] = true;
  next[HPRMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return enforceModuleDependencies(next).modules;
}

/**
 * If moduleKey is turned OFF, return dependents that are still ON and would break.
 */
export function getBlockingDependents(moduleKey, modules) {
  if (!moduleKey || !modules) return [];
  return HPRMS_MODULE_DEPENDENCIES.filter(
    (rule) =>
      rule.requires.includes(moduleKey) && modules[rule.moduleKey] !== false
  ).map((rule) => ({
    key: rule.moduleKey,
    label: moduleLabel(rule.moduleKey),
    reason: rule.reason,
  }));
}

/**
 * If moduleKey is turned ON, return required modules that are currently OFF.
 */
export function getMissingRequirements(moduleKey, modules) {
  const rule = HPRMS_MODULE_DEPENDENCIES.find((r) => r.moduleKey === moduleKey);
  if (!rule) return [];
  return rule.requires
    .filter((req) => modules?.[req] === false)
    .map((req) => ({
      key: req,
      label: moduleLabel(req),
      reason: rule.reason,
    }));
}

/** Hover text when a module cannot be turned OFF. */
export function getTurnOffBlockMessage(moduleKey, modules) {
  const blockers = getBlockingDependents(moduleKey, modules);
  if (!blockers.length) return "";
  const names = blockers.map((b) => b.label).join(", ");
  return `${moduleLabel(moduleKey)} stays on while ${names} is enabled. ${blockers[0].reason} Turn off ${names} first if you want to disable this.`;
}

/**
 * Apply dependency rules: auto-enable missing requirements.
 * Does not turn modules off — only fills gaps so flows stay valid.
 */
export function enforceModuleDependencies(modules) {
  const next = { ...(modules || getDefaultModules()) };
  const enabled = [];
  let changed = true;
  // Multi-pass for chains (e.g. QI → GR → PO)
  while (changed) {
    changed = false;
    HPRMS_MODULE_DEPENDENCIES.forEach((rule) => {
      if (next[rule.moduleKey] === false) return;
      rule.requires.forEach((req) => {
        if (next[req] === false) {
          next[req] = true;
          enabled.push(moduleLabel(req));
          changed = true;
        }
      });
    });
  }
  next[HPRMS_MODULE_KEYS.DASHBOARD] = true;
  next[HPRMS_MODULE_KEYS.MODULE_SETTINGS] = true;
  return {
    modules: next,
    autoEnabledLabels: [...new Set(enabled)],
  };
}

/**
 * Try toggle. Returns { ok, modules, message }.
 * Turning OFF a required dep while dependents are ON → blocked.
 * Turning ON a module with missing deps → auto-enables deps.
 */
export function tryToggleModule(modules, moduleKey, enable) {
  const current = { ...(modules || getDefaultModules()) };
  if (
    moduleKey === HPRMS_MODULE_KEYS.DASHBOARD ||
    moduleKey === HPRMS_MODULE_KEYS.MODULE_SETTINGS
  ) {
    return {
      ok: false,
      modules: current,
      message: `${moduleLabel(moduleKey)} stays always on.`,
    };
  }

  if (!enable) {
    const blockers = getBlockingDependents(moduleKey, current);
    if (blockers.length) {
      return {
        ok: false,
        modules: current,
        message: getTurnOffBlockMessage(moduleKey, current),
      };
    }
    current[moduleKey] = false;
    return { ok: true, modules: current, message: "" };
  }

  current[moduleKey] = true;
  const enforced = enforceModuleDependencies(current);
  const autoMsg = enforced.autoEnabledLabels.length
    ? `Also turned on: ${enforced.autoEnabledLabels.join(", ")} (needed for this module).`
    : "";
  return {
    ok: true,
    modules: enforced.modules,
    message: autoMsg.trim(),
  };
}
